// ---------------------------------------------------------------------------
// useRelationshipHighlight — computes BFS-connected node set + usage details
// for a selected node in the data relationship graph.
// ---------------------------------------------------------------------------

import { useMemo } from 'react'
import { useAppOntology } from '@/features/flow-editor/hooks/use-app-ontology'
import { useVariableUsageIndex, useDatasourceUsageIndex } from './use-usage-index'
import type { OntologyEdge } from '@/features/flow-editor/services/app-ontology'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UsageDetails {
  flowCount: number
  bindingCount: number
  readCount: number
  writeCount: number
}

export interface RelationshipHighlight {
  connectedSet: Set<string> | null
  usageDetails: UsageDetails | null
}

// ---------------------------------------------------------------------------
// BFS traversal — find all upstream + downstream connected nodes
// (Copied from data-flow-canvas.tsx since it is a local function there)
// ---------------------------------------------------------------------------

function findConnectedSet(
  selectedId: string,
  edges: OntologyEdge[],
): Set<string> {
  const connected = new Set<string>([selectedId])
  const edgesBySource = new Map<string, OntologyEdge[]>()
  const edgesByTarget = new Map<string, OntologyEdge[]>()

  for (const e of edges) {
    if (!edgesBySource.has(e.source)) edgesBySource.set(e.source, [])
    edgesBySource.get(e.source)!.push(e)
    if (!edgesByTarget.has(e.target)) edgesByTarget.set(e.target, [])
    edgesByTarget.get(e.target)!.push(e)
  }

  // BFS upstream (follow edges where target = current, walk to source)
  const upQueue = [selectedId]
  while (upQueue.length > 0) {
    const current = upQueue.pop()!
    const incoming = edgesByTarget.get(current) ?? []
    for (const e of incoming) {
      if (!connected.has(e.source)) {
        connected.add(e.source)
        upQueue.push(e.source)
      }
    }
  }

  // BFS downstream (follow edges where source = current, walk to target)
  const downQueue = [selectedId]
  while (downQueue.length > 0) {
    const current = downQueue.pop()!
    const outgoing = edgesBySource.get(current) ?? []
    for (const e of outgoing) {
      if (!connected.has(e.target)) {
        connected.add(e.target)
        downQueue.push(e.target)
      }
    }
  }

  return connected
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useRelationshipHighlight(
  selectedNodeId: string | null,
): RelationshipHighlight {
  const ontology = useAppOntology()
  const variableUsageIndex = useVariableUsageIndex()
  const datasourceUsageIndex = useDatasourceUsageIndex()

  return useMemo(() => {
    if (!selectedNodeId) {
      return { connectedSet: null, usageDetails: null }
    }

    const connectedSet = findConnectedSet(selectedNodeId, ontology.edges)

    // Compute usage details based on node type
    let usageDetails: UsageDetails | null = null

    if (selectedNodeId.startsWith('state:')) {
      // Extract variable key from "state:{blockName}:{key}"
      const parts = selectedNodeId.split(':')
      const key = parts.slice(2).join(':')
      const entries = variableUsageIndex.get(key) ?? []
      usageDetails = {
        flowCount: entries.filter((e) => e.flowName).length,
        bindingCount: entries.filter((e) => e.mode === 'binding').length,
        readCount: entries.filter((e) => e.mode === 'read').length,
        writeCount: entries.filter((e) => e.mode === 'write').length,
      }
    } else if (selectedNodeId.startsWith('ds:')) {
      // Extract datasource name from "ds:{blockName}:{flowName}:{index}"
      const parts = selectedNodeId.split(':')
      const dsName = parts.length >= 4 ? parts[2] : parts[1]
      const entries = datasourceUsageIndex.get(dsName) ?? []
      usageDetails = {
        flowCount: entries.filter((e) => e.usage === 'fetch').length,
        bindingCount: entries.filter((e) => e.usage === 'binding').length,
        readCount: 0,
        writeCount: 0,
      }
    }

    return { connectedSet, usageDetails }
  }, [selectedNodeId, ontology.edges, variableUsageIndex, datasourceUsageIndex])
}
